import { useEffect, useMemo, useState } from "react";
import {
  Database, ExternalLink, RefreshCw, Search, Filter, Lock, Globe2,
  CheckCircle2, AlertCircle, Sparkles, Clock, Zap, ChevronDown, ChevronRight, PlayCircle,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import WebhookHealthPanel from "@/components/WebhookHealthPanel";

const CATEGORIES = [
  { v: "", l: "All categories" },
  { v: "business", l: "Business" },
  { v: "property", l: "Property" },
  { v: "land", l: "Land" },
  { v: "demographics", l: "Demographics" },
  { v: "environment", l: "Environment" },
  { v: "owner", l: "Owner & litigation" },
  { v: "scoring", l: "Scoring" },
];

const ASSET_TYPES = [
  { v: "", l: "All asset types" },
  { v: "business", l: "Business" },
  { v: "multifamily", l: "Multifamily" },
  { v: "land", l: "Land" },
];

const STATUS_STYLE = {
  automated: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  scaffolded: "bg-amber-500/10 text-amber-300 border-amber-500/30",
  click_through: "bg-sky-500/10 text-sky-300 border-sky-500/30",
  manual: "bg-slate-500/10 text-slate-400 border-white/10",
};

const STATUS_LABEL = {
  automated: "Live",
  scaffolded: "Not yet ingesting",
  click_through: "Click-through",
  manual: "Manual",
};

export default function DataSources() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const isAdminOrSuper = user?.role === "admin" || user?.role === "super_admin";

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reseeding, setReseeding] = useState(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [assetType, setAssetType] = useState("");

  const load = () => {
    setLoading(true);
    const params = {};
    if (category) params.category = category;
    if (assetType) params.asset_type = assetType;
    api
      .get("/data-sources", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => toast.error("Failed to load data sources"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [category, assetType]);

  const reseed = async () => {
    setReseeding(true);
    try {
      const r = await api.post("/data-sources/seed");
      toast.success(`Inserted ${r.data.inserted}, skipped ${r.data.skipped}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-seed failed");
    } finally {
      setReseeding(false);
    }
  };

  const filtered = useMemo(() => {
    if (!query.trim()) return items;
    const q = query.trim().toLowerCase();
    return items.filter(
      (i) =>
        i.name.toLowerCase().includes(q) ||
        (i.description || "").toLowerCase().includes(q) ||
        (i.jurisdiction || "").toLowerCase().includes(q)
    );
  }, [items, query]);

  const stats = useMemo(() => {
    const automated = items.filter((i) => i.integration_status === "automated").length;
    const free = items.filter((i) => i.free_tier).length;
    return { total: items.length, automated, free };
  }, [items]);

  return (
    <div className="space-y-6" data-testid="data-sources-page">
      {isAdminOrSuper && <WebhookHealthPanel />}
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-400 mb-2">Admin · Catalog</div>
          <h1 className="font-heading text-4xl tracking-tight">Data Sources</h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Curated, legally accessible public data feeds for business, land, and multifamily acquisitions.
            Automated sources enrich opportunities in real time; others are click-through references for due diligence.
          </p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={reseed}
            disabled={reseeding}
            data-testid="reseed-catalog-btn"
            className="btn-ghost text-sm inline-flex items-center gap-2"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${reseeding ? "animate-spin" : ""}`} />
            {reseeding ? "Seeding…" : "Re-seed curated catalog"}
          </button>
        )}
      </header>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Sources in catalog" value={stats.total} icon={Database} testid="ds-stat-total" />
        <Stat label="Automated integrations" value={stats.automated} icon={Sparkles} accent="text-emerald-400" testid="ds-stat-automated" />
        <Stat label="Free tier available" value={stats.free} icon={Globe2} testid="ds-stat-free" />
      </div>

      <LastRunDashboard isSuperAdmin={isSuperAdmin} />

      <div className="ae-card p-4 flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[220px]">
          <Search className="h-3.5 w-3.5 text-slate-500" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, jurisdiction, description…"
            data-testid="ds-search-input"
            className="flex-1 bg-transparent text-sm outline-none placeholder:text-slate-600"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter className="h-3.5 w-3.5 text-slate-500" />
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            data-testid="ds-category-select"
            className="bg-[#0A0D12] border border-white/10 rounded-sm px-2.5 py-1.5 text-xs"
          >
            {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
          <select
            value={assetType}
            onChange={(e) => setAssetType(e.target.value)}
            data-testid="ds-asset-type-select"
            className="bg-[#0A0D12] border border-white/10 rounded-sm px-2.5 py-1.5 text-xs"
          >
            {ASSET_TYPES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="ds-loading">Loading catalog…</div>
      ) : filtered.length === 0 ? (
        <div className="ae-card p-8 text-center text-slate-500" data-testid="ds-empty">
          No data sources match your filters.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3" data-testid="ds-list">
          {filtered.map((src) => (
            <SourceCard key={src.sid} src={src} />
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <div className="ae-card p-5" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div className="text-[10px] uppercase tracking-widest text-slate-500">{label}</div>
        <Icon className="h-4 w-4 text-slate-500" />
      </div>
      <div className={`font-mono tabular-nums text-3xl font-bold mt-2 ${accent || "text-slate-100"}`}>{value}</div>
    </div>
  );
}

function SourceCard({ src }) {
  const status = src.integration_status || "manual";
  const cls = STATUS_STYLE[status] || STATUS_STYLE.manual;
  return (
    <article className="ae-card p-5 flex flex-col gap-3" data-testid={`ds-card-${src.sid}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500">
            <span>{src.category}</span>
            <span className="opacity-40">·</span>
            <span>{src.jurisdiction}</span>
          </div>
          <h3 className="font-heading text-base mt-1 leading-snug" data-testid={`ds-name-${src.sid}`}>{src.name}</h3>
        </div>
        <span
          className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${cls}`}
          data-testid={`ds-status-${src.sid}`}
          title={status === "scaffolded"
            ? "Architecture is in place but the connector is not yet pulling live data. This source is on the Priority 3 backlog and currently contributes zero rows to discovery."
            : status.replace("_", " ")}
        >
          {STATUS_LABEL[status] || status.replace("_", " ")}
        </span>
      </div>

      <p className="text-xs text-slate-400 leading-relaxed line-clamp-4">{src.description}</p>

      <div className="flex flex-wrap items-center gap-2 mt-auto pt-2 border-t border-white/5">
        {(src.asset_types || []).map((t) => (
          <span key={t} className="text-[10px] uppercase tracking-wider bg-white/[0.03] border border-white/10 text-slate-300 rounded-sm px-1.5 py-0.5">{t}</span>
        ))}
        <span className="flex-1" />
        {src.free_tier ? (
          <span className="inline-flex items-center gap-1 text-[10px] text-emerald-400">
            <CheckCircle2 className="h-3 w-3" /> free tier
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] text-amber-400">
            <AlertCircle className="h-3 w-3" /> paid
          </span>
        )}
        {src.requires_auth && (
          <span className="inline-flex items-center gap-1 text-[10px] text-slate-400">
            <Lock className="h-3 w-3" /> key
          </span>
        )}
      </div>

      {src.legal_notes && (
        <div className="text-[11px] text-slate-500 italic">{src.legal_notes}</div>
      )}

      <a
        href={src.url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`ds-open-${src.sid}`}
        className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300"
      >
        <ExternalLink className="h-3.5 w-3.5" /> Open portal
      </a>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Last-Run Dashboard  (surfaces county-connector health at a glance)
// ---------------------------------------------------------------------------
const BUCKET_COLORS = {
  ok: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10",
  stale: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  failed: "text-rose-300 border-rose-500/40 bg-rose-500/10",
  never: "text-slate-400 border-white/15 bg-white/5",
  disabled: "text-slate-500 border-white/10 bg-transparent",
};

function fmtRelative(iso) {
  if (!iso) return ", ";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const abs = Math.abs(diffMs);
  const min = Math.round(abs / 60000);
  const hr = Math.round(abs / 3600000);
  const day = Math.round(abs / 86400000);
  const label = day >= 1 ? `${day}d` : hr >= 1 ? `${hr}h` : `${min}m`;
  return diffMs >= 0 ? `${label} ago` : `in ${label}`;
}

function LastRunDashboard({ isSuperAdmin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [filter, setFilter] = useState("all");
  const [running, setRunning] = useState(false);
  const [runningCid, setRunningCid] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/discovery/configs/health");
      setData(r.data);
    } catch {
      // silent ,  admin-only
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // 30s refresh
    return () => clearInterval(id);
  }, []);

  const runNow = async () => {
    if (!isSuperAdmin) return;
    setRunning(true);
    try {
      const r = await api.post("/admin/discovery/configs/scheduler/run-now");
      toast.success(`Fired: due=${r.data.due} · ran=${r.data.ran} · failed=${r.data.failed}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Manual run failed");
    } finally {
      setRunning(false);
    }
  };

  const runOne = async (cid) => {
    if (!isSuperAdmin) return;
    setRunningCid(cid);
    try {
      const r = await api.post(`/admin/discovery/configs/${cid}/run`);
      const err = r?.data?.error;
      if (err) toast.error(`Ran ${cid}: ${err}`);
      else toast.success(`Ran ${cid}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunningCid(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="ae-card p-4 text-sm text-slate-500" data-testid="last-run-dashboard-loading">
        <Clock className="h-3.5 w-3.5 inline mr-2 animate-pulse" /> Loading connector health…
      </div>
    );
  }
  if (!data || !data.rows) return null;

  const rows = filter === "all" ? data.rows : data.rows.filter((r) => r.bucket === filter);
  const buckets = data.buckets || {};
  const sched = data.scheduler || {};

  return (
    <div className="ae-card p-5 space-y-4" data-testid="last-run-dashboard">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-400">
            <Zap className="h-3 w-3" /> Live · APScheduler
          </div>
          <h2 className="font-heading text-2xl tracking-tight mt-1">Connector Last Run</h2>
          <div className="text-xs text-slate-500 mt-1">
            {sched.running
              ? <>Scheduler running · next tick <span className="text-emerald-300 font-mono">{fmtRelative(sched.next_tick)}</span> ({new Date(sched.next_tick).toLocaleTimeString()})</>
              : <span className="text-rose-300">Scheduler is not running ,  automated pulls are paused</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="last-run-refresh"
            className="btn-ghost text-xs inline-flex items-center gap-1.5"
            title="Refresh"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
          {isSuperAdmin && (
            <button
              onClick={runNow}
              disabled={running}
              data-testid="last-run-fire-now"
              className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 disabled:opacity-40 inline-flex items-center gap-1.5"
              title="Manually fire the scheduler now (runs every due config)"
            >
              <PlayCircle className={`h-3 w-3 ${running ? "animate-pulse" : ""}`} />
              {running ? "Firing…" : "Fire now"}
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid="last-run-buckets">
        {[
          { k: "all", label: `All ${data.total}`, cls: "text-slate-300 border-white/20" },
          { k: "failed", label: `Failed ${buckets.failed || 0}`, cls: BUCKET_COLORS.failed },
          { k: "stale", label: `Stale ${buckets.stale || 0}`, cls: BUCKET_COLORS.stale },
          { k: "never", label: `Never ${buckets.never || 0}`, cls: BUCKET_COLORS.never },
          { k: "ok", label: `OK ${buckets.ok || 0}`, cls: BUCKET_COLORS.ok },
          { k: "disabled", label: `Disabled ${buckets.disabled || 0}`, cls: BUCKET_COLORS.disabled },
        ].map((chip) => (
          <button
            key={chip.k}
            onClick={() => setFilter(chip.k)}
            data-testid={`last-run-filter-${chip.k}`}
            className={`px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm border ${chip.cls} ${
              filter === chip.k ? "ring-1 ring-emerald-400/50" : "opacity-70 hover:opacity-100"
            }`}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div className="border border-white/8 rounded-sm overflow-hidden">
        <div className="max-h-[380px] overflow-y-auto">
          <table className="w-full text-xs" data-testid="last-run-table">
            <thead className="bg-white/3 text-slate-400 uppercase tracking-widest text-[9px] sticky top-0 z-10">
              <tr>
                <th className="text-left px-3 py-2">Connector</th>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Schedule</th>
                <th className="text-left px-3 py-2">Last run</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-left px-3 py-2">Next tick</th>
                {isSuperAdmin && <th className="text-right px-3 py-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {(expanded ? rows : rows.slice(0, 12)).map((r) => (
                <tr
                  key={r.cid}
                  data-testid={`last-run-row-${r.cid}`}
                  className="border-t border-white/5 hover:bg-white/2"
                >
                  <td className="px-3 py-2">
                    <div className="text-slate-200">{r.county || ", "}, {r.state || ", "}</div>
                    <div className="text-[9px] text-slate-500 uppercase tracking-widest">{r.category}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-400 font-mono text-[10px]">{r.source_type}</td>
                  <td className="px-3 py-2 text-slate-400">{r.schedule || ", "}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <div className="text-slate-300 font-mono text-[10px]">{fmtRelative(r.last_run)}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span
                      className={`inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-widest rounded-sm border ${BUCKET_COLORS[r.bucket]}`}
                      title={r.last_message || ""}
                    >
                      {r.bucket}
                    </span>
                    {r.last_message && r.bucket === "failed" && (
                      <div className="text-[9px] text-rose-400/80 mt-0.5 max-w-[240px] truncate" title={r.last_message}>
                        {r.last_message}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.is_due ? (
                      <span className="text-[9px] uppercase tracking-widest text-emerald-300">
                        Pending next tick
                      </span>
                    ) : (
                      <span className="text-slate-400 font-mono text-[10px]">
                        {fmtRelative(r.next_scheduled_tick)}
                      </span>
                    )}
                  </td>
                  {isSuperAdmin && (
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => runOne(r.cid)}
                        disabled={runningCid === r.cid || !r.enabled}
                        data-testid={`last-run-row-fire-${r.cid}`}
                        className="text-[10px] uppercase tracking-wider text-emerald-300 hover:text-emerald-200 disabled:text-slate-600 inline-flex items-center gap-1"
                      >
                        <PlayCircle className={`h-3 w-3 ${runningCid === r.cid ? "animate-pulse" : ""}`} />
                        Run
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={isSuperAdmin ? 7 : 6} className="px-3 py-6 text-center text-slate-500">
                    No connectors in this bucket.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {rows.length > 12 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="last-run-expand"
            className="w-full text-[10px] uppercase tracking-widest text-slate-400 hover:text-slate-200 py-1.5 border-t border-white/8 inline-flex items-center justify-center gap-1"
          >
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
            {expanded ? "Show fewer" : `Show all ${rows.length}`}
          </button>
        )}
      </div>
    </div>
  );
}

